/**
 * Новости: статус публикации, черновик карточки и то, что мешает сохранить.
 * Правила полей — в `@dorixona/api/news` (контракт для бэкенда).
 */
import { isApiError } from '@dorixona/api'
import { NEWS_BODY_MAX, NEWS_LEAD_MAX, NEWS_TITLE_MAX } from '@dorixona/api/news'
import { addDaysMs, isoString, localDateTime, parseLocalDateTime, parseMs } from '@dorixona/utils'
import { failureText } from '../../ui/Load'
import type { News, NewsToSave } from './api'

export type NewsStatus = 'draft' | 'scheduled' | 'published'

export function newsStatus(news: Pick<News, 'published' | 'publishedAt'>, now: number): NewsStatus {
  if (!news.published) return 'draft'
  return news.publishedAt !== null && parseMs(news.publishedAt) > now ? 'scheduled' : 'published'
}

export const STATUS_TEXT: Record<NewsStatus, string> = {
  draft: 'черновик',
  scheduled: 'запланирована',
  published: 'опубликована',
}

export const STATUS_TONE: Record<NewsStatus, string | undefined> = {
  draft: undefined,
  scheduled: 'info',
  published: 'success',
}

export type Lang = 'ru' | 'uz'

/** Всё, что в карточке сохраняется одной кнопкой. */
export interface NewsDraft {
  titleRu: string
  titleUz: string
  leadRu: string
  leadUz: string
  bodyRu: string
  bodyUz: string
  photoKey: string
  photoUrl: string
  published: boolean
  /** Значение <input type="datetime-local">; пусто — дата не выбрана. */
  at: string
}

export function toDraft(news: News): NewsDraft {
  return {
    titleRu: news.titleRu,
    titleUz: news.titleUz,
    leadRu: news.leadRu,
    leadUz: news.leadUz,
    bodyRu: news.bodyRu,
    bodyUz: news.bodyUz,
    photoKey: news.photoKey,
    photoUrl: news.photoUrl,
    published: news.published,
    at: news.publishedAt ? localDateTime(parseMs(news.publishedAt)) : '',
  }
}

export function fromDraft(id: number, draft: NewsDraft): NewsToSave {
  const at = parseLocalDateTime(draft.at)
  return {
    id,
    titleRu: draft.titleRu.trim(),
    titleUz: draft.titleUz.trim(),
    leadRu: draft.leadRu.trim(),
    leadUz: draft.leadUz.trim(),
    bodyRu: draft.bodyRu,
    bodyUz: draft.bodyUz,
    photoKey: draft.photoKey,
    photoUrl: draft.photoUrl,
    published: draft.published,
    publishedAt: at === null ? null : isoString(at),
  }
}

/** Поля текста по языкам. */
export const TEXTS: Record<
  Lang,
  { title: keyof NewsDraft; lead: keyof NewsDraft; body: keyof NewsDraft }
> = {
  ru: { title: 'titleRu', lead: 'leadRu', body: 'bodyRu' },
  uz: { title: 'titleUz', lead: 'leadUz', body: 'bodyUz' },
}

/** Видимый текст HTML: пустой абзац редактора — это не текст. */
export function hasText(html: string): boolean {
  return (
    html
      .replace(/<img\b[^>]*>/gi, 'img')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim() !== ''
  )
}

/** Сколько полей языка заполнено — видно на переключателе языка. */
export function filled(draft: NewsDraft, lang: Lang): number {
  const { title, lead, body } = TEXTS[lang]
  return [
    String(draft[title]).trim() !== '',
    String(draft[lead]).trim() !== '',
    hasText(String(draft[body])),
  ].filter(Boolean).length
}

/** Сервер меряет текст в байтах UTF-8: кириллица — два байта на букву. */
const bytes = (text: string): number => new TextEncoder().encode(text).length

/** Как выйдет новость: черновик, сразу или по расписанию. */
export type PublishMode = 'draft' | 'now' | 'scheduled'

export const PUBLISH_MODES: [PublishMode, string][] = [
  ['draft', 'Черновик'],
  ['now', 'Сразу'],
  ['scheduled', 'По расписанию'],
]

/** Режим следует из черновика: опубликована с датой в будущем — по расписанию. */
export function publishMode(draft: Pick<NewsDraft, 'published' | 'at'>, now: number): PublishMode {
  if (!draft.published) return 'draft'
  const at = parseLocalDateTime(draft.at)
  return at !== null && at > now ? 'scheduled' : 'now'
}

/**
 * Переключение режима. «Сразу» ставит дату «сейчас», если её нет или она в
 * будущем; «по расписанию» — завтра в 9:00, если дата не в будущем. Черновик
 * дату не трогает: её вернут при публикации.
 */
export function withMode(draft: NewsDraft, mode: PublishMode, now: number): Partial<NewsDraft> {
  if (mode === 'draft') return { published: false }
  const at = parseLocalDateTime(draft.at)
  if (mode === 'now') {
    return { published: true, at: at !== null && at <= now ? draft.at : localDateTime(now) }
  }
  const tomorrow = `${localDateTime(addDaysMs(now, 1)).slice(0, 10)}T09:00`
  return { published: true, at: at !== null && at > now ? draft.at : tomorrow }
}

export interface Requirement {
  label: string
  ok: boolean
}

/** Что нужно, чтобы новость вышла: видно галочками, пока её публикуют. */
export function publishChecklist(draft: NewsDraft): Requirement[] {
  return [
    { label: 'Заголовок на русском', ok: draft.titleRu.trim() !== '' },
    { label: 'Фото', ok: draft.photoKey !== '' },
    { label: 'Текст на русском', ok: hasText(draft.bodyRu) },
    { label: 'Дата публикации', ok: parseLocalDateTime(draft.at) !== null },
  ]
}

/** Что мешает сохранить — первая причина; пусто, если можно. Те же правила, что у сервера. */
export function draftProblem(draft: NewsDraft): string {
  if (!draft.titleRu.trim()) return 'Укажите заголовок на русском'
  if (draft.titleRu.length > NEWS_TITLE_MAX || draft.titleUz.length > NEWS_TITLE_MAX) {
    return `Заголовок — не длиннее ${NEWS_TITLE_MAX} символов`
  }
  if (draft.leadRu.length > NEWS_LEAD_MAX || draft.leadUz.length > NEWS_LEAD_MAX) {
    return `Анонс — не длиннее ${NEWS_LEAD_MAX} символов`
  }
  if (bytes(draft.bodyRu) > NEWS_BODY_MAX || bytes(draft.bodyUz) > NEWS_BODY_MAX) {
    return 'Текст слишком длинный — разделите его на несколько новостей'
  }
  if (draft.published) {
    if (!draft.photoKey) return 'Чтобы опубликовать, добавьте фото'
    if (!hasText(draft.bodyRu)) return 'Чтобы опубликовать, напишите текст на русском'
    if (parseLocalDateTime(draft.at) === null) return 'Укажите дату публикации'
  }
  return ''
}

/**
 * Пустая новость для формы создания: черновик без текста, фото и даты. Её
 * заполняют той же формой, что и правку, и сохраняют целиком.
 */
export function newNews(): News {
  return {
    id: 0,
    titleRu: '',
    titleUz: '',
    leadRu: '',
    leadUz: '',
    bodyRu: '',
    bodyUz: '',
    photoKey: '',
    photoUrl: '',
    published: false,
    publishedAt: null,
    createdAt: '',
    updatedAt: '',
  }
}

/** Причину отказа проверки называем как есть; остальное — как везде в админке. */
export function newsFailureText(error: unknown): string {
  if (
    isApiError(error) &&
    (error.code === 'validation_failed' || error.code === 'payload_too_large') &&
    error.message !== error.code
  ) {
    return error.message
  }
  return failureText(error)
}
